import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { connect } from "react-redux";
import InputFull from "../../Components/Common/InuptFull";
import Menu from "../../Components/Template/Menu";
import MenuTrainer from "../../Components/Template/MenuTrainer";
import Template from "../../Components/Template/Template";
import {
  ErrorMsgToaster,
  SuccessMsgToaster,
} from "../../Components/Utils/Toaster";
import Education from "./Education";
import {
  changePassword,
  getCategories,
  getGoals,
  getProfile,
  updateProfile,
  updateProfilePic,
  updateTrainerBankDetails,
} from "./ProfileApi";
import { FaCamera, FaEye } from "react-icons/fa";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import ThemeContext from "../../Context/ThemeContext";
import {
  EducationDetails,
  BankDetailsType,
  StudentProfile,
} from "../../Utils/types";

type GoalsType = {
  goalName: string;
  isSelected: boolean;
};

const StudentProfileComp = (props: any) => {
  const [studentDetail, setStudentDetail] = useState<StudentProfile>();
  const [registerType, setRegisterType] = useState<string>("");
  const [categoryList, setCategoryList] = useState<Array<string>>([]);
  const [goalList, setGoalList] = useState<Array<GoalsType>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [firstRender, setFirstRender] = useState(true);
  const [bankDetails, setBankDetails] = useState<BankDetailsType>();

  useEffect(() => {
    getCategories()
      .then((resData: Array<string>) => resData?.length && setCategoryList(resData))
      .catch(() => ErrorMsgToaster("Failed to fetch categories"));
  }, []);

  useEffect(() => {
    const { registerType } = props.userInfo;
    if (registerType && firstRender) {
      setFirstRender(false);
      setRegisterType(registerType);
      getProfile(registerType)
        .then((data) => {
          setStudentDetail(data);
          setSelectedCategory(data?.category ?? "");
          data && handleGoals(data?.goal, data?.category);
        })
        .catch(() => ErrorMsgToaster("Failed to load profile"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userInfo]);

  const handleGoals = (selectedGoals: string[] = [], category: string) => {
    getGoals({ category })
      .then((resData: string[]) => {
        const list = resData?.map((rec: string) => ({
          goalName: rec,
          isSelected: selectedGoals.includes(rec),
        }));
        setGoalList(list);
      })
      .catch(() => ErrorMsgToaster("Failed to load goals"));
  };

  return (
    <Template
      menu={registerType === "Student" ? <Menu selectedItem={`MY PROFILE`} /> : <MenuTrainer selectedItem={`MY PROFILE`} />}
      body={
        <StudentProfilesbody
          registerType={registerType}
          studentDetail={studentDetail}
          setStudentDetail={setStudentDetail}
          goalsList={goalList}
          setGoalList={setGoalList}
          categoryList={categoryList}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          bankDetails={bankDetails}
        />
      }
    />
  );
};

const StudentProfilesbody = ({
  studentDetail,
  setStudentDetail,
  registerType,
  goalsList,
  setGoalList,
  categoryList,
  selectedCategory,
  setSelectedCategory,
  bankDetails,
}: {
  studentDetail?: StudentProfile;
  setStudentDetail?: Dispatch<SetStateAction<StudentProfile | undefined>>;
  registerType: string;
  goalsList: Array<GoalsType>;
  setGoalList: Dispatch<SetStateAction<GoalsType[]>>;
  categoryList: Array<string>;
  selectedCategory: string;
  setSelectedCategory: Dispatch<SetStateAction<string>>;
  bankDetails?: BankDetailsType;
}) => {
  const updateField = (field: Object) => {
    setStudentDetail?.({ ...studentDetail, ...field });
  };

  const safe = (val: any) => val ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePic, setProfilePic] = useState<any>();

  useEffect(() => {
    setProfilePic(studentDetail?.profileUrl ?? "");
  }, [studentDetail]);

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    getGoals({ category: value })
      .then((resData) => {
        const data = resData?.map((rec: string) => ({
          goalName: rec,
          isSelected: false,
        })) ?? [];
        setGoalList(data);
      })
      .catch(() => ErrorMsgToaster("Failed to update goals"));
  };

  const changeSelectedGoals = (value: string) => {
    setGoalList((prev) =>
      prev.map((rec) =>
        rec.goalName === value ? { ...rec, isSelected: !rec.isSelected } : rec
      )
    );
  };

  const handleUpdateProfile = () => {
    const goal = goalsList.filter((rec) => rec.isSelected).map((val) => val.goalName);
    updateProfile(registerType, {
      ...studentDetail,
      category: selectedCategory,
      goal,
    })
      .then((data) => data?.userId ? SuccessMsgToaster("Updated Successfully") : ErrorMsgToaster("Try again later"))
      .catch(() => ErrorMsgToaster("Update Failed"));
  };

  const updateBankDetailsInfo = () => {
    if (!bankDetails) return;
    updateTrainerBankDetails(bankDetails)
      .then(() => SuccessMsgToaster("Bank Details Updated"))
      .catch(() => ErrorMsgToaster("Failed updating bank details"));
  };

  return (
    <ThemeContext.Consumer>
      {(context) => (
        <div className="flex flex-col text-gray-700">
          <div className="-mt-8 text-2xl font-bold" style={{ color: context.theme?.title }}>
            My Profile
          </div>

          {!profilePic ? (
            <label
              htmlFor="myInput"
              className="w-32 h-32 border border-gray-400 bg-gray-100 rounded-full flex justify-center items-center self-center mb-6 cursor-pointer"
            >
              <FaCamera />
              <input
                className="hidden"
                id="myInput"
                type="file"
                accept=".jpg, .jpeg"
                onChange={(e) => {
                  if (!e.target.files) return;
                  const file = e.target.files[0];
                  if (!["jpg", "jpeg"].includes(file.name.split(".").pop()!))
                    return ErrorMsgToaster("Upload jpg/jpeg image");
                  const formData = new FormData();
                  formData.append("image", file);
                  updateProfilePic(registerType, formData)
                    .then((resData) => setProfilePic(resData.profileUrl))
                    .catch(() => ErrorMsgToaster("Upload failed"));
                }}
              />
            </label>
          ) : (
            <>
              <img src={profilePic} alt="profile pic" className="w-32 h-32 border border-gray-400 rounded-full self-center mb-2" />
              <button className="pl-2 pr-2 pt-1 pb-1 rounded bg-red-300 text-black text-lg self-center mb-6" onClick={() => setProfilePic("")}>
                Change Profile Picture
              </button>
            </>
          )}

          {/* FORM FIELDS WITH ?? "" FIX */}
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-6 w-full">
            <InputFull type="text" label="First Name" value={safe(studentDetail?.firstName)} onChange={(e) => updateField({ firstName: e.target.value })} />
            <InputFull type="text" label="Last Name" value={safe(studentDetail?.lastName)} onChange={(e) => updateField({ lastName: e.target.value })} />
          </div>

          <div className="grid sm:grid-cols-2 grid-cols-1 gap-6 w-full mt-2">
            <InputFull type="email" disabled label="Email" value={safe(studentDetail?.email)} />
            <InputFull
              type="tel"
              label="Mobile"
              maxLength={10}
              value={safe(studentDetail?.mobileNumber)}
              onChange={(e) => updateField({ mobileNumber: e.target.value.replace(/[^0-9]/g, "") })}
            />
          </div>

          <div className="mt-2">
            <InputFull type="text" label="Address" value={safe(studentDetail?.address)} onChange={(e) => updateField({ address: e.target.value })} />
          </div>

          <div className="grid sm:grid-cols-2 grid-cols-1 gap-6 w-full mt-2">
            <div>
              <h3>Country</h3>
              <CountryDropdown value={safe(studentDetail?.country)} classes="w-full border border-gray-400 rounded mt-1 p-2 h-12 bg-white" onChange={(value) => updateField({ country: value })} />
            </div>
            <div>
              <h3>State</h3>
              <RegionDropdown country={safe(studentDetail?.country)} value={safe(studentDetail?.state)} classes="w-full border border-gray-400 rounded mt-1 p-2 h-12 bg-white" onChange={(val) => updateField({ state: val })} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 grid-cols-1 gap-6 w-full mt-2">
            <InputFull type="text" label="City" value={safe(studentDetail?.city)} onChange={(e) => updateField({ city: e.target.value })} />
            <InputFull type="text" label="Pincode" maxLength={6} value={safe(studentDetail?.pincode)} onChange={(e) => updateField({ pincode: e.target.value.replace(/[^0-9]/g, "") })} />
          </div>

          <div className="mt-2">
            <InputFull type="text" label="Skill" value={safe(studentDetail?.skills)} onChange={(e) => updateField({ skills: e.target.value })} />
          </div>

          <div className="mt-2">
            <h3>Category</h3>
            <select
              value={selectedCategory}
              className="w-full border border-gray-400 rounded mt-1 p-2 h-12 bg-white"
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {categoryList?.map((goal, index) => (
                <option key={index} value={goal}>
                  {goal}
                </option>
              ))}
            </select>
          </div>

          <div className="md:text-base sm:text-sm text-xs mt-4">
            {goalsList.map((value) => (
              <button
                key={value.goalName}
                style={{
                  background: value.isSelected ? context.theme!.button.background : "white",
                  color: value.isSelected ? "white" : "black",
                  borderColor: value.isSelected ? context.theme!.button.background : "black",
                }}
                className="h-auto py-4 w-auto px-4 mr-4 mb-3 border-2 rounded-md"
                onClick={() => changeSelectedGoals(value.goalName)}
              >
                {value.goalName}
              </button>
            ))}
          </div>

          <button className="mt-5 text-white text-sm h-10 w-32 rounded-md font-bold place-self-end" style={{ backgroundColor: context.theme!.button.background }} onClick={handleUpdateProfile}>
            UPDATE
          </button>

          {/* EDUCATION */}
          <div className="mt-8 font-bold">Education</div>
          {studentDetail?.education?.map((detail: EducationDetails) => (
            <Education key={detail.field + detail.institutionName} detail={detail} />
          ))}
          <button className="text-white text-xs h-10 w-32 rounded-md font-bold place-self-end mt-5" style={{ backgroundColor: context.theme!.button.background }} onClick={() => {
            const dummy = { field: "", institutionName: "", duration: "", courseName: "" };
            studentDetail?.education ? updateField({ education: [...studentDetail.education, dummy] }) : updateField({ education: [dummy] });
          }}>ADD EDUCATION</button>

          {/* PASSWORD */}
          <div className="mt-5 font-bold">Change Password</div>
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-6 w-full mt-2">
            <div className="relative">
              <InputFull type={showChangePassword ? "text" : "password"} label="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <FaEye className="absolute right-3 top-8 cursor-pointer" onClick={() => setShowChangePassword(!showChangePassword)} />
            </div>
            <div className="relative">
              <InputFull type={showConfirmPassword ? "text" : "password"} label="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              <FaEye className="absolute right-3 top-8 cursor-pointer" onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
            </div>
          </div>

          <button
            className="text-white text-sm h-10 w-32 rounded-md font-bold place-self-end mt-8"
            style={{ backgroundColor: context.theme!.button.background }}
            onClick={() => {
              if (password !== confirmPassword) return ErrorMsgToaster("Confirm password mismatch");
              changePassword({ password, confirmPassword })
                .then(() => SuccessMsgToaster("Password reset Successfully"))
                .catch(() => ErrorMsgToaster("Failed to update password"));
            }}
          >
            UPDATE
          </button>
        </div>
      )}
    </ThemeContext.Consumer>
  );
};

const mapStateToProps = (state: any) => ({
  userInfo: state?.userInfoReducer,
});

export default connect(mapStateToProps, null)(StudentProfileComp);
